import { L2Dialog, L2DialogActions } from "../l2/L2Dialog"
import { useCallback, useEffect, useRef, useState } from "react"
import _ from 'lodash'
import { DrugConfirmation, DrugProduct, Prescription } from "@fusion-advantage/backcompat"
import { newPrescription } from "@fusion-advantage/formlib"
import { SelectedResultRow, CollapsedContentContainer, MedReconDialogContentContainer, SearchResultRow, SearchResultRowActions, SearchResultRowText, SearchResultsContainer, SearchResultsList } from "../l1/L1MedicationReconcilliation"
import { L1ReasonableButton, L1SmallIconButton } from "../l1/L1Button"
import styled from "@emotion/styled"
import { FusionColor } from "../../../theme/FusionStyle"
import { faMagnifyingGlass, faPlus, faTrashCan, faXmark } from "@fortawesome/pro-solid-svg-icons"
import L1TextField from "../l1/L1TextField"
import { ComplexElementSize } from "../ComplexElementSize"
import L3LazyLoadedList from "./L3LazyLoadedList"
import { V2L1SmallRoundIcon } from "../../uiv2/Indicator/V2L1SmallRoundIcon"
import { faClock } from "@fortawesome/pro-regular-svg-icons"

type L3MedReconManualEntryModalProps = {
	models: Prescription[],
	drugsList: DrugProduct[],
	onSave: (models: Prescription[]) => void,
	onClose: () => void,
	show: boolean,
	priorIds?: number[]
}

const PriorDrugResult = styled.div(({theme}) => ({
	width: '45px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	background: theme.colors.second,
	overflow: 'hidden',
}))

const L3MedReconManualEntryModal = ({models, drugsList, onSave, onClose, show, priorIds = []}: L3MedReconManualEntryModalProps) => {
	const [searchResults, setSearchResults] = useState<DrugProduct[]>([])
	const [searchTerm, setSearchTerm] = useState('')
	const [manualModels, setManualModels] = useState<Prescription[]>([])
	const textFieldRef = useRef<HTMLInputElement>(null)

	const debouncedSearchTerm = useRef(_.debounce((term, drugs: DrugProduct[]) => {
		let results: any[] = []

		if (term) {
			const filteredDrugs = drugs.filter(drug => drug.Name?.toLowerCase().startsWith(term.toLowerCase()))
			const sortedDrugs = _.sortBy(filteredDrugs, drug => priorIds.includes(drug.ID) === false)
			results = sortedDrugs
		}

		setSearchResults(results || [])
	}, 300))
	
	useEffect(() => {
		debouncedSearchTerm.current(searchTerm, drugsList)
	}, [searchTerm, drugsList])

	useEffect(() => {
		if(show) {
			textFieldRef.current?.focus()
		}
	}, [show])

	const handleChange = useCallback((drugId: number, drugName: string) => {
		const product: DrugProduct = {
			ID: drugId,
			Name: drugName,
		}
		const presc = newPrescription(product)

		presc.Confirmation = DrugConfirmation.ManualEntry
		presc.InUse = true,
		presc.IsPrior = false

		setManualModels((prev) => [...prev, presc])
	}, [manualModels])

	const handleRemove = useCallback((drugId: number) => {
		const modelsTemp = _.cloneDeep(manualModels)
		const index = modelsTemp.findIndex(x => x.Product.ID === drugId)
		
		if(index > -1) {
			modelsTemp.splice(index, 1)
	
			setManualModels(modelsTemp)
		}
	}, [manualModels])

	const isAdded = useCallback((drugId: number): boolean => {
		const existing = manualModels.find(x => x.Product.ID === drugId) ||
			models.find(x => x.Product.ID === drugId && x.Confirmation != null)

		return !!existing
	}, [models, manualModels])

	const handleSave = useCallback(() => {
		onSave(manualModels)
		setSearchTerm('')
		setManualModels([])
		onClose()
	}, [onSave, manualModels])

	/*
	const handleClose = useCallback(() => {
		onClose()
		setSearchTerm('')
	}, [onClose])
	*/

	return (
		<L2Dialog 
			title='Drug Search'
			show={show} 
			size="max"
			headerProps={{ showBoxShadow: true }}
			showCloseButton={false}
		>
			<MedReconDialogContentContainer>
				<SearchResultsContainer>
					{
						manualModels.length > 0 && (
							<CollapsedContentContainer>
								{
									manualModels.map(drug => (
										<SelectedResultRow key={drug.Product.ID}>
											<div>{drug.Product.Name}</div>
											<L1SmallIconButton color={FusionColor.BlueMain} icon={faTrashCan} onClick={() => handleRemove(drug.Product.ID)} />
										</SelectedResultRow>
									))	
								}
							</CollapsedContentContainer>
						)
					}
					<L1TextField 
						inputRef={textFieldRef}
						prefixIcon={faMagnifyingGlass} 
						size={ComplexElementSize.FlexStretch} 
						onChange={(e) => setSearchTerm(e.target.value)} 
						value={searchTerm} 
						tip={
							searchTerm !== '' &&
							<L1SmallIconButton 
								icon={faXmark} 
								color={FusionColor.BlueMain} 
								onClick={() => setSearchTerm('')} 
							/>
						} 
					/>	
					{
						searchResults.length > 0 && (
							<SearchResultsList>
								<L3LazyLoadedList<DrugProduct>
									items={searchResults}
									itemIcrement={10}
									builder={(model, idx) => {
										return (
											<SearchResultRow showDivider={idx > 0}>
												{
													priorIds.includes(model.ID) && (
														<PriorDrugResult>
															<V2L1SmallRoundIcon backgroundColor={FusionColor.Third} icon={faClock} />
														</PriorDrugResult>
													)
												}
												<SearchResultRowText>
													{model.Name}
												</SearchResultRowText>
												<SearchResultRowActions>
													<L1ReasonableButton disabled={isAdded(model.ID)} wide color={FusionColor.BlueMain} label={isAdded(model.ID) ? "Added" : "Add"} icon={faPlus} onClick={() => handleChange(model.ID, model.Name)} />
												</SearchResultRowActions>
											</SearchResultRow>
										)
									}}
								/>
							</SearchResultsList>
						)
					}
				</SearchResultsContainer>
			</MedReconDialogContentContainer>
			<L2DialogActions justifyContent="center" showBoxShadow>
				<L1ReasonableButton wide label="Done" onClick={handleSave} />
			</L2DialogActions>
    	</L2Dialog>
	)
}

export default L3MedReconManualEntryModal